import { useState } from 'react';
import { Button } from '@/components/ui/Button';

interface Step6Props {
  onNext: (data: Step6Data) => void;
  onBack: () => void;
  initialData?: Step6Data;
}

export interface Step6Data {
  integrations: string[];
  customIntegrations?: string;
}

const commonIntegrations = [
  { value: 'payment_gateway', label: 'Payment Gateways', description: 'PayFast, Stripe, PayPal' },
  { value: 'crm', label: 'CRM Systems', description: 'Salesforce, HubSpot, Zoho' },
  { value: 'email', label: 'Email Services', description: 'SendGrid, Mailchimp, Gmail' },
  { value: 'social', label: 'Social Media', description: 'Facebook, Twitter, Instagram' },
  { value: 'accounting', label: 'Accounting', description: 'Xero, QuickBooks, Sage' },
  { value: 'custom_api', label: 'Custom API', description: 'Your existing systems' },
];

export const Step6Integrations = ({ onNext, onBack, initialData }: Step6Props) => {
  const [selectedIntegrations, setSelectedIntegrations] = useState<string[]>(
    initialData?.integrations || []
  );
  const [customIntegrations, setCustomIntegrations] = useState(
    initialData?.customIntegrations || ''
  );

  const toggleIntegration = (integration: string) => {
    setSelectedIntegrations((prev) =>
      prev.includes(integration)
        ? prev.filter((i) => i !== integration)
        : [...prev, integration]
    );
  };

  const handleSubmit = () => {
    onNext({ integrations: selectedIntegrations, customIntegrations });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Integrations</h2>
        <p className="text-gray-600 mt-2">
          What external tools or services need to be connected?
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {commonIntegrations.map((integration) => (
          <div
            key={integration.value}
            onClick={() => toggleIntegration(integration.value)}
            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
              selectedIntegrations.includes(integration.value)
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:border-blue-300'
            }`}
          >
            <div className="flex items-start">
              <div className="flex-shrink-0 mt-1">
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    selectedIntegrations.includes(integration.value)
                      ? 'bg-blue-600 border-blue-600'
                      : 'border-gray-300'
                  }`}
                >
                  {selectedIntegrations.includes(integration.value) && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 12 12">
                      <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" fill="none" />
                    </svg>
                  )}
                </div>
              </div>
              <div className="ml-3">
                <h4 className="font-semibold text-gray-900">{integration.label}</h4>
                <p className="text-sm text-gray-600">{integration.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div>
        <label htmlFor="custom" className="block text-sm font-medium text-gray-700 mb-2">
          Other Integrations (Optional)
        </label>
        <textarea
          id="custom"
          value={customIntegrations}
          onChange={(e) => setCustomIntegrations(e.target.value)}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Describe any other services or APIs you need to integrate with..."
        />
      </div>

      <div className="flex justify-between">
        <Button onClick={onBack} variant="outline" className="px-8">
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          className="bg-blue-600 hover:bg-blue-700 text-white px-8"
        >
          Next
        </Button>
      </div>
    </div>
  );
};
